import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Course } from '../../types';
import ConfirmModal from '../../components/common/ConfirmModal';
import RichText from '../../components/common/RichText';
import { stripHtml } from '../../utils/html';
import { useToast } from '../../hooks/useToast';

export default function CourseApprovals() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    loadCourses();
  }, []);

  async function loadCourses() {
    try {
      setLoading(true);
      const response = await api.get('/admin/course-approvals');
      setCourses(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t('courseApprovals.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }

  function handleApprove(courseId: string) {
    setConfirmModal({
      isOpen: true,
      title: t('courseApprovals.approveTitle'),
      message: t('courseApprovals.approveMessage'),
      variant: 'info',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await api.post(`/admin/courses/${courseId}/approve`);
          loadCourses();
          setSelectedCourse(null);
        } catch (err: any) {
          toast.error(err.response?.data?.error || t('courseApprovals.failedToApprove'));
        } finally {
          setProcessing(false);
          setConfirmModal(null);
        }
      },
    });
  }

  async function handleReject(courseId: string) {
    if (!rejectionReason.trim()) {
      toast.warning(t('courseApprovals.pleaseProvideReason'));
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/admin/courses/${courseId}/reject`, {
        reason: rejectionReason,
      });
      loadCourses();
      setSelectedCourse(null);
      setRejectionReason('');
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('courseApprovals.failedToReject'));
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('courseApprovals.title')}</h1>
          <p className="text-gray-500 mt-1">
            {t('courseApprovals.subtitle')}
          </p>
        </div>
        {courses.length > 0 && (
          <span className="bg-yellow-50 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium">
            {t('courseApprovals.pendingCount', { count: courses.length })}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">{error}</div>
      )}

      {courses.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-lg">
          <p className="text-gray-500">{t('courseApprovals.noPending')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {courses.map((course) => (
            <div
              key={course._id}
              className="glass-card rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex gap-6">
                {course.thumbnail && (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-48 h-32 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">
                        {course.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {t('courseApprovals.by', { name: course.creator?.name || 'Unknown', email: course.creator?.email })}
                      </p>
                    </div>
                    <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full text-sm">
                      {t('courseApprovals.pendingReview')}
                    </span>
                  </div>

                  <p className="text-gray-500 line-clamp-2 mb-3">{stripHtml(course.description)}</p>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span>{t('courseApprovals.categoryLabel', { value: course.category || 'N/A' })}</span>
                    <span>{t('courseApprovals.durationLabel', { value: course.duration || 'N/A' })}</span>
                    <span>{t('courseApprovals.priceLabel', { value: course.price || 0 })}</span>
                    <span>{t('courseApprovals.lessonsLabel', { count: course.lessonCount || 0 })}</span>
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      {t('courseApprovals.viewDetails')}
                    </button>
                    <button
                      onClick={() => handleApprove(course._id)}
                      disabled={processing}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {t('common.approve')}
                    </button>
                    <button
                      onClick={() => setSelectedCourse(course)}
                      disabled={processing}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {t('common.reject')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCourse.title}
                </h2>
                <button
                  onClick={() => {
                    setSelectedCourse(null);
                    setRejectionReason('');
                  }}
                  className="text-gray-500 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-6">
                {selectedCourse.thumbnail && (
                  <img
                    src={selectedCourse.thumbnail}
                    alt={selectedCourse.title}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                )}

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">{t('courseApprovals.descriptionLabel')}</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    <RichText html={selectedCourse.description} />
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">{t('common.category')}</h3>
                    <p className="text-gray-500">{selectedCourse.category || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">{t('common.duration')}</h3>
                    <p className="text-gray-500">{selectedCourse.duration || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">{t('common.price')}</h3>
                    <p className="text-gray-500">${selectedCourse.price || 0}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">{t('courseApprovals.lessonsLabel', { count: selectedCourse.lessonCount || 0 })}</h3>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-2">{t('courseApprovals.instructorLabel')}</h3>
                  <p className="text-gray-500">
                    {selectedCourse.creator?.name} ({selectedCourse.creator?.email})
                  </p>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      {t('courseApprovals.rejectReasonLabel')}
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold"
                      rows={3}
                      placeholder={t('courseApprovals.rejectReasonPlaceholder')}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(selectedCourse._id)}
                      disabled={processing}
                      className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {processing ? t('common.processing') : t('courseApprovals.approveCourse')}
                    </button>
                    <button
                      onClick={() => handleReject(selectedCourse._id)}
                      disabled={processing || !rejectionReason.trim()}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {processing ? t('common.processing') : t('courseApprovals.rejectCourse')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          confirmText={t('common.approve')}
        />
      )}
    </div>
  );
}
